"use client";
import { useRef, useEffect } from "react";
import { select, scaleOrdinal, pie, arc } from "d3";
import styles from "./styles.module.css";
import useElementSize from "@/app/hooks/useElementSize";
import {
  formatNumberToAccountingValue,
  formatNumberToPercentValue,
} from "@/app/utils/intl";
import { COLOR_PALETTE } from "@/app/utils/constants";

export default function DonutChart({
  data,
}: {
  data: {
    [key: string]: {
      label: string;
      value: number;
    };
  };
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(chartRef);

  useEffect(() => {
    // Check clause
    if (!width) return;
    if (!chartRef.current) return;
    if (!data) return;

    const margin = 16;
    const outerWidth = 328;
    const outerHeight = outerWidth;
    const innerWidth = outerWidth - margin;
    const innerHeight = outerHeight - margin;
    const radius = Math.min(innerWidth, innerHeight) / 2 - margin;
    const colors = COLOR_PALETTE.slice(3);

    let total = 0;
    for (let [_, value] of Object.entries(data)) {
      total += value.value;
    }

    // Color scale
    const color = scaleOrdinal().range(colors);

    // Render the chart
    const svg = select(svgRef.current)
      .data([null])
      .join("svg")
      .classed("svg_chart", true)
      .attr("width", outerWidth)
      .attr("height", outerHeight)
      .attr("viewBox", [0, 0, outerWidth, outerHeight]);

    // Group
    const g = svg
      .selectAll(".svg_g")
      .data([null])
      .join("g")
      .classed("svg_g", true)
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("transform", `translate(${innerWidth / 2},${innerHeight / 2})`);

    // Compute the donut
    const donut = pie().value((d: any) => d[1]);
    const donutData: any[] = [];
    for (const [key, value] of Object.entries(data)) {
      donutData.push([key, value?.value]);
    }
    const pieData = donut(donutData);

    // The arc generator
    const mainArc: any = arc()
      .innerRadius(radius * 0.5) // This is the size of the donut hole
      .outerRadius(radius * 1);

    // Another arc that won't be drawn. Just for labels positioning
    const outerArc: any = arc()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    // Build the pie slices
    g.selectAll(".g__slice")
      .data(pieData)
      .join("path")
      .classed("g__slice", true)
      .attr("d", mainArc)
      .style("stroke", (d: any) => color(d.data[0]) as string)
      .style("fill", (d: any) => `${color(d.data[0])}80`);

    // Add labels
    g.selectAll(".g__label")
      .data(pieData)
      .join("text")
      .classed("g__label", true)
      .attr("transform", (d: any) => {
        const pos = mainArc.centroid(d);
        return `translate(${pos})`;
      })
      .text((d: any) => formatNumberToPercentValue(d.data[1] / total) || 0)
      .style("font-size", (d: any) => {
        const { startAngle, endAngle } = d;
        const diff = endAngle - startAngle;
        return Math.min(diff * 12, 12);
      })
      .style("font-weight", "500")
      .style("text-anchor", "middle")
      .style("alignment-baseline", "middle")
      .style("fill", "var(--black)");

    // Create tooltip
    const tooltip = select(tooltipRef.current);

    // Build the slice overlay
    g.selectAll(".g_overlay")
      .data(pieData)
      .join("path")
      .classed("g_overlay", true)
      .attr("d", mainArc)
      .style("stroke", "transparent")
      .style("fill", "transparent")
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d: any) {
        select(this).style("stroke", "var(--blue)").style("fill", "#141a9940");
        const addr = d?.data[0] || "";
        const value = formatNumberToAccountingValue(data[addr].value) || 0;
        const percent =
          formatNumberToPercentValue(data[addr].value / total) || 0;
        const label = data[addr].label || "";

        // Add tooltip
        tooltip.style("display", "block").html(
          `<div>
              <span>Holder</span>
              <span>${label}</span>
          </div>
          <div>
              <span>Tokens</span>
              <span>${value} (${percent})</span>
          </div>`,
        );

        const posX = event.offsetX;
        const posY = event.offsetY - 16;
        const moveX = "0%";
        const moveY = "-100%";

        // Position tooltip
        tooltip
          .style("transform", `translate(${moveX},${moveY})`)
          .style("top", `${posY}px`)
          .style("left", `${posX}px`);
      })
      .on("mousemove", function (event) {
        const posX = event.offsetX;
        const posY = event.offsetY - 16;
        const moveX = "0%";
        const moveY = "-100%";

        // Position tooltip
        tooltip
          .style("transform", `translate(${moveX},${moveY})`)
          .style("top", `${posY}px`)
          .style("left", `${posX}px`);
      })
      .on("mouseleave", function () {
        select(this)
          .style("stroke", "transparent")
          .style("fill", "transparent");

        // Remove tooltip
        tooltip
          .style("display", "none")
          .style("transform", "translate(0,0)")
          .style("top", 0)
          .style("left", 0)
          .html("");
      })
      .on("click", function (_: any, d: any) {
        const addr = d?.data[0] || "";
        if (!addr || addr === "others") return;

        // Redirect to profile
        const href = `/profile/${addr}`;
        window.location.href = href;
      });

    // Create legend
    const legend = select(legendRef.current);

    // Iterate over legend keys
    let legendHtml = "";
    for (let [objKey, objValue] of Object.entries(data)) {
      const { value = 0, label = "" } = objValue;
      const percent = formatNumberToPercentValue(value / total);
      const color = `${colors[Object.keys(data).indexOf(objKey)]}80`;
      legendHtml += `
          <div class="${styles.donutChartLegendItem}">
            <div class="${styles.donutChartLegendColor}" style="background: ${color}"></div>
            <div class="${styles.donutChartLegendLabel}">${label}</div>
            <div class="${styles.donutChartLegendValue}">${percent}</div>
          </div>
        `;
    }
    legend.html(legendHtml);

    // Clean up
    return () => {
      svg.selectAll("*").remove();
    };
  }, [width, data]);

  return (
    <div className={styles.donutChartContainer}>
      <div className={styles.donutChart} ref={chartRef}>
        <svg ref={svgRef}></svg>
        <div className={styles.donutChartTooltip} ref={tooltipRef}></div>
      </div>
      <div className={styles.donutChartLegend} ref={legendRef}></div>
    </div>
  );
}
